import { useCallback, useEffect, useMemo, useState } from 'react';
import { Layout } from '@/components/Layout';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { formatDate, statusLabel, statusColor } from '@/lib/format';
import { useSEO } from '@/lib/useSEO';
import { downloadXlsx } from '@/lib/download';
import { toast } from 'sonner';
import { ADMIN } from '@/constants/testIds';
import {
  FileDown, Search, Eye, CheckCircle2, XCircle, AlertTriangle, Circle,
  Clock, FileText, Landmark, Shield,
} from 'lucide-react';
import { Pagination, usePagination } from '@/components/Pagination';
import { ApproveWithoutDocs } from '@/components/ApproveWithoutDocs';

const DOC_STATUS_META = {
  missing:          { label: 'Pendiente',           icon: Circle,        cls: 'text-slate-400', badge: 'bg-slate-100 text-slate-700 border-slate-200' },
  uploaded:         { label: 'En revisión',         icon: Clock,         cls: 'text-blue-500',  badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  approved:         { label: 'Aprobado',            icon: CheckCircle2,  cls: 'text-emerald-500', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  rejected:         { label: 'Rechazado',           icon: XCircle,       cls: 'text-rose-500',  badge: 'bg-rose-50 text-rose-700 border-rose-200' },
  needs_correction: { label: 'Corrección',          icon: AlertTriangle, cls: 'text-amber-500', badge: 'bg-amber-50 text-amber-800 border-amber-200' },
};

export default function AdminSellers() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('pending');
  const [q, setQ] = useState('');
  const [detail, setDetail] = useState(null);   // { seller, user, checklist }
  const [reviewing, setReviewing] = useState({}); // {docType: notes}

  useSEO({
    title: 'Vendedores | Administración',
    description: 'Aprueba o rechaza solicitudes, revisa documentos y consulta datos bancarios cifrados.',
    canonical: '/admin/vendedores',
  });

  const fetchData = useCallback(() => {
    const params = status !== 'all' ? { status } : {};
    api.get('/admin/sellers', { params }).then(({ data }) => setItems(data.items || []));
  }, [status]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((x) => (
      (x.business_name || '').toLowerCase().includes(s) ||
      (x.nit || '').toLowerCase().includes(s) ||
      (x.user?.email || '').toLowerCase().includes(s) ||
      (x.user?.name || '').toLowerCase().includes(s)
    ));
  }, [items, q]);
  const { pageItems, page, setPage, totalPages, total } = usePagination(filtered);

  const openDetail = async (uid) => {
    try {
      const { data } = await api.get(`/admin/sellers/${uid}/detail`);
      setDetail(data);
      setReviewing({});
    } catch (e) {
      toast.error('No se pudo cargar el detalle');
    }
  };
  const closeDetail = () => setDetail(null);

  const approve = async (uid) => {
    try {
      await api.put(`/admin/sellers/${uid}/approve`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo aprobar'); return;
    }
    toast.success('Vendedor aprobado'); fetchData(); if (detail?.seller?.user_id === uid) openDetail(uid);
  };
  const reject = async (uid) => {
    await api.put(`/admin/sellers/${uid}/reject`);
    toast.success('Vendedor rechazado'); fetchData(); if (detail?.seller?.user_id === uid) openDetail(uid);
  };

  const reviewDoc = async (docType, action) => {
    const uid = detail.seller.user_id;
    const notes = reviewing[docType] || '';
    try {
      await api.patch(`/admin/sellers/${uid}/documents/${docType}`, { action, notes });
      toast.success('Documento revisado');
      setReviewing({ ...reviewing, [docType]: '' });
      const { data } = await api.get(`/admin/sellers/${uid}/detail`);
      setDetail(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-orange-600">Administración</div>
            <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1">Vendedores</h1>
          </div>
          <div className="flex gap-2 flex-wrap">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2 top-2.5 text-slate-400" />
              <Input
                data-testid="admin-sellers-search"
                placeholder="Buscar por nombre, NIT o email"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="rounded-none pl-8 w-72"
              />
            </div>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="admin-sellers-filter" className="w-48 rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="incomplete">Incompletos</SelectItem>
                <SelectItem value="pending">Pendientes</SelectItem>
                <SelectItem value="approved">Aprobados</SelectItem>
                <SelectItem value="rejected">Rechazados</SelectItem>
              </SelectContent>
            </Select>
            <Button
              data-testid="export-sellers-btn"
              onClick={() => downloadXlsx('/admin/exports/sellers', 'vendedores.xlsx')}
              variant="outline" className="rounded-none"
            >
              <FileDown className="mr-2 h-4 w-4" /> Exportar
            </Button>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {pageItems.map((s) => (
            <div key={s.user_id} className="border border-slate-200 bg-white p-5" data-testid={`admin-seller-card-${s.user_id}`}>
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="font-display font-bold text-lg text-slate-900 truncate">
                    {s.business_name || s.user?.name || '—'}
                  </div>
                  <div className="text-xs text-slate-500 font-mono mt-0.5 truncate">{s.user?.email}</div>
                  <div className="mt-1 text-sm text-slate-700">NIT: {s.nit || '—'}</div>
                </div>
                <Badge className={`rounded-none border ${statusColor[s.status]}`}>{statusLabel[s.status]}</Badge>
              </div>

              <div className="mt-3 text-xs text-slate-500">
                Suscripción: <span className="font-bold uppercase">{statusLabel[s.subscription_status] || s.subscription_status}</span> · Registrado {formatDate(s.created_at)}
              </div>

              <div className="mt-4 flex gap-2 flex-wrap">
                <Button
                  data-testid={`admin-seller-detail-${s.user_id}`}
                  onClick={() => openDetail(s.user_id)}
                  variant="outline" className="rounded-none"
                >
                  <Eye className="mr-2 h-4 w-4" /> Ver perfil
                </Button>
                {s.status === 'pending' && (
                  <>
                    <Button
                      data-testid={ADMIN.approveSellerBtn(s.user_id)}
                      onClick={() => approve(s.user_id)}
                      className="rounded-none bg-emerald-700 hover:bg-emerald-800"
                    >
                      Aprobar
                    </Button>
                    <Button
                      data-testid={ADMIN.rejectSellerBtn(s.user_id)}
                      onClick={() => reject(s.user_id)}
                      variant="outline" className="rounded-none border-rose-300 text-rose-700 hover:bg-rose-50"
                    >
                      Rechazar
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
          {pageItems.length === 0 && (
            <div className="col-span-full p-8 text-center text-slate-500 border border-slate-200 bg-slate-50">
              Sin vendedores en este estado
            </div>
          )}
        </div>
        <Pagination page={page} totalPages={totalPages} total={total} setPage={setPage} testIdPrefix="admin-sellers-pag" />

        <SellerDetailDialog
          open={!!detail}
          detail={detail}
          onClose={closeDetail}
          onReview={reviewDoc}
          onApproveSeller={approve}
          onRejectSeller={reject}
          onOverrideDone={() => { fetchData(); openDetail(detail.seller.user_id); }}
          reviewing={reviewing}
          setReviewing={setReviewing}
        />
      </div>
    </Layout>
  );
}

const SellerDetailDialog = ({ open, detail, onClose, onReview, onApproveSeller, onRejectSeller, onOverrideDone, reviewing, setReviewing }) => {
  if (!detail) return null;
  const { seller, user, checklist } = detail;
  const banking = seller?.banking;
  const labels = checklist.labels;
  const allDocs = [...checklist.required_doc_types, ...checklist.optional_doc_types];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-none max-w-4xl max-h-[90vh] overflow-y-auto bg-white" data-testid="admin-seller-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-4">
            <span>{seller.business_name || user.name || 'Vendedor'}</span>
            <Badge className={`rounded-none border ${statusColor[seller.status]}`}>{statusLabel[seller.status]}</Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="border border-slate-200 p-3">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Contacto</div>
            <div className="mt-1">{user.name}</div>
            <div className="text-slate-600">{user.email}</div>
            <div className="text-slate-600">{user.phone || '—'} · {user.city || '—'}</div>
            <div className="text-slate-500 text-xs mt-1">Registro: {formatDate(user.created_at)}</div>
          </div>
          <div className="border border-slate-200 p-3">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Negocio</div>
            <div className="mt-1">{seller.business_name || '—'}</div>
            <div className="text-slate-600">NIT: {seller.nit || '—'}</div>
            <div className="text-slate-600">{seller.address || '—'}</div>
          </div>
        </div>

        {/* Progreso checklist */}
        <div className="mt-4 border border-slate-200 p-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Progreso de verificación</div>
              <div className="text-sm font-semibold">
                {checklist.required_docs_approved} / {checklist.required_docs_total} documentos aprobados · Banking: {checklist.banking_saved ? 'guardado' : 'pendiente'}
              </div>
            </div>
            <Badge className={`rounded-none ${checklist.activation_ready ? 'bg-emerald-600' : 'bg-slate-500'}`}>
              {checklist.activation_ready ? 'Listo para activar' : 'En proceso'}
            </Badge>
          </div>
        </div>

        {/* Docs con acciones */}
        <div className="mt-4 space-y-3">
          {allDocs.map((docType) => {
            const doc = checklist.documents[docType] || { status: 'missing' };
            const meta = DOC_STATUS_META[doc.status] || DOC_STATUS_META.missing;
            const Icon = meta.icon;
            const isOptional = checklist.optional_doc_types.includes(docType);
            return (
              <div key={docType} data-testid={`admin-doc-${docType}`} className="border border-slate-200 p-3">
                <div className="flex items-start gap-3">
                  <Icon className={`h-5 w-5 shrink-0 mt-0.5 ${meta.cls}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold text-slate-900">{labels[docType]}</div>
                      {isOptional && <span className="text-[10px] text-slate-500">(opcional)</span>}
                      <Badge className={`rounded-none border ${meta.badge}`} variant="outline">{meta.label}</Badge>
                    </div>
                    {doc.admin_notes && (
                      <div className="mt-1 text-xs text-slate-600 bg-slate-50 border border-slate-200 p-2">
                        Nota anterior: {doc.admin_notes}
                      </div>
                    )}
                    {doc.url && (
                      <a
                        href={doc.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-blue-700 hover:underline inline-flex items-center gap-1 mt-2"
                      >
                        <FileText className="h-3.5 w-3.5" /> Ver / Descargar archivo
                      </a>
                    )}
                    {doc.status !== 'missing' && (
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-2">
                        <Textarea
                          placeholder="Nota (opcional)"
                          value={reviewing[docType] || ''}
                          onChange={(e) => setReviewing({ ...reviewing, [docType]: e.target.value })}
                          rows={1}
                          className="rounded-none md:col-span-4 text-sm"
                          data-testid={`admin-doc-note-${docType}`}
                        />
                        <Button
                          size="sm" className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => onReview(docType, 'approve')}
                          data-testid={`admin-doc-approve-${docType}`}
                        >
                          Aprobar
                        </Button>
                        <Button
                          size="sm" variant="outline"
                          className="rounded-none border-amber-300 text-amber-700 hover:bg-amber-50"
                          onClick={() => onReview(docType, 'request_correction')}
                          data-testid={`admin-doc-correction-${docType}`}
                        >
                          Solicitar corrección
                        </Button>
                        <Button
                          size="sm" variant="outline"
                          className="rounded-none border-rose-300 text-rose-700 hover:bg-rose-50"
                          onClick={() => onReview(docType, 'reject')}
                          data-testid={`admin-doc-reject-${docType}`}
                        >
                          Rechazar
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Banking */}
        <div className="mt-4 border border-slate-200 p-3" data-testid="admin-banking-block">
          <div className="flex items-center gap-2">
            <Landmark className="h-4 w-4 text-slate-700" />
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Información bancaria</div>
            <Badge variant="outline" className="rounded-none border-emerald-200 bg-emerald-50 text-emerald-700 text-[10px]">
              <Shield className="h-3 w-3 mr-1" /> Cifrada
            </Badge>
          </div>
          {banking ? (
            <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
              <div><div className="text-[10px] text-slate-500">Banco</div><div className="font-semibold">{banking.bank_name}</div></div>
              <div><div className="text-[10px] text-slate-500">Tipo</div><div className="font-semibold capitalize">{banking.account_type}</div></div>
              <div><div className="text-[10px] text-slate-500">Cuenta</div><div className="font-mono">{banking.account_number_masked || '—'}</div></div>
              <div><div className="text-[10px] text-slate-500">Titular</div><div className="font-semibold">{banking.account_holder}</div></div>
            </div>
          ) : (
            <div className="mt-2 text-sm text-slate-500">El vendedor aún no ha guardado sus datos bancarios.</div>
          )}
        </div>

        {/* Actions on the seller as a whole */}
        {seller.status === 'pending' && (
          <div className="mt-4 flex gap-2 flex-wrap border-t border-slate-200 pt-4">
            <Button className="rounded-none bg-emerald-700 hover:bg-emerald-800" onClick={() => onApproveSeller(seller.user_id)}>
              Aprobar vendedor
            </Button>
            <Button variant="outline" className="rounded-none border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => onRejectSeller(seller.user_id)}>
              Rechazar vendedor
            </Button>
          </div>
        )}
        {seller.status !== 'approved' && !checklist.activation_ready && (
          <div className="mt-4 flex border-t border-slate-200 pt-4">
            <ApproveWithoutDocs sellerId={seller.user_id} bankingSaved={checklist.banking_saved} onDone={onOverrideDone} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
